import React, { useMemo, useEffect, useRef, type ReactNode } from 'react';
import { styled } from '@compiled/react';
import { Field as AkField } from '@atlaskit/form';
import CalendarIcon from '@atlaskit/icon/glyph/calendar';
import InlineDialog from '@atlaskit/inline-dialog';
import AkInlineEdit from '@atlaskit/inline-edit';
import AkSelect, { type StylesConfig } from '@atlaskit/select';
import { token } from '@atlaskit/tokens';
import { ff } from '@atlassian/jira-feature-flagging';
import { useIntl } from '@atlassian/jira-intl';
import type { CalendarSelectOption } from '../../../../../../../../../common/types';
import {
	toSubGoalCalendarFieldName,
	toGoalCalendarFieldName,
} from '../../../../../../../../../common/utils/goal/field';
import { useSLAHierarchicalGoalsEnabled } from '../../../../../../../../../controllers/enable-sla-hierarchical-goals';
import { useMetricResource } from '../../../../../../../../../controllers/metric-resource';
import messages from './messages';
import { formatOptions } from './utils';

type Props = {
	isEditing: boolean;
	focusOnEdit?: boolean;
	id: string;
	goalId?: string;
	calendarId?: number;
	onEditRequested: () => void;
	onChange?: () => void;
};

export const CalendarSelectField = ({
	id,
	goalId,
	isEditing,
	onEditRequested,
	focusOnEdit = false,
	calendarId,
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	onChange = () => {},
}: Props) => {
	const { formatMessage } = useIntl();
	const [{ data }] = useMetricResource();
	const [{ isSLAHierarchicalGoalsEnabled }] = useSLAHierarchicalGoalsEnabled();
	let fieldName;
	if (isSLAHierarchicalGoalsEnabled) {
		fieldName = toSubGoalCalendarFieldName(goalId, id);
	} else {
		fieldName = toGoalCalendarFieldName(id, isEditing);
	}
	const calendarOptions = useMemo(() => formatOptions(data?.calendarRefs ?? []), [data]);

	const akSelectRef = useRef<unknown>(null);

	useEffect(() => {
		// @ts-expect-error - TS2571 - Object is of type 'unknown'.
		isEditing && focusOnEdit && akSelectRef.current?.focus();
		// @ts-expect-error - TS2339 - Property 'blur' does not exist on type 'Element'.
		!isEditing && document?.activeElement?.blur();
	}, [focusOnEdit, isEditing]);

	const defaultValue = useMemo(() => {
		const selectedCalendarOption = calendarOptions.find((option) => calendarId === option.value);

		if (selectedCalendarOption) {
			return selectedCalendarOption;
		}

		const defaultCalendarOption = calendarOptions.find(({ isDefaultOption }) => isDefaultOption);

		return defaultCalendarOption;
	}, [calendarId, calendarOptions]);

	return (
		// @ts-expect-error - TS2322 - Type '{ children: ({ fieldProps: { value, ...rest } }: { fieldProps: FieldProps<CalendarSelectOption, HTMLInputElement>; error?: string | undefined; valid: boolean; meta: Meta; }) => Element; focus: true; name: string; defaultValue: CalendarSelectOption | undefined; }' is not assignable to type 'IntrinsicAttributes & FieldComponentProps<CalendarSelectOption, HTMLInputElement>'.
		<AkField focus name={fieldName} defaultValue={defaultValue}>
			{({ fieldProps: { value, ...rest } }) => (
				// @ts-expect-error - TS2741 - Property 'defaultValue' is missing in type '{ editView: ({ errorMessage, isInvalid }: ExtendedFieldProps<string>) => Element; readView: () => Element; isEditing: boolean; onConfirm: () => void; onEdit: () => void; hideActionButtons: true; readViewFitContainerWidth: true; }' but required in type 'InlineEditProps<string>'.
				<AkInlineEdit
					editView={({ errorMessage, isInvalid }) =>
						ff('fix-jsma11y-980-issue') ? (
							<InlineDialog
								isOpen={isInvalid}
								content={<div>{errorMessage}</div>}
								placement="right"
							>
								<div aria-labelledby="accessible-id-for-calendar-field">
									<AkSelect
										// @ts-expect-error - TS2322 - Type 'MutableRefObject<unknown>' is not assignable to type '((string | ((instance: { components: Partial<SelectComponents<CalendarSelectOption, false, GroupTypeBase<CalendarSelectOption>>>; ... 23 more ...; UNSAFE_componentWillUpdate?(nextProps: Readonly<...>, nextState: Readonly<...>, nextContext: any): void; } | null) => void) | RefObject<...>) & (((instance: any) => void)...'.
										ref={akSelectRef}
										inputId={rest.id}
										// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
										options={calendarOptions as CalendarSelectOption[]}
										components={{
											ValueContainer,
										}}
										aria-label={formatMessage(messages.selectCalendar)}
										// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
										value={value as CalendarSelectOption}
										data-testid="servicedesk-sla-settings.ui.metrics.metric.editable.form.goals.goal.fields.calendar.ak-select"
										{...rest}
										onChange={(...args) => {
											// @ts-expect-error - TS2554 - Expected 1 arguments, but got 2.
											rest.onChange(...args);
											onChange();
										}}
										appearance={isSLAHierarchicalGoalsEnabled ? 'subtle' : 'default'}
										styles={customStyles}
									/>
								</div>
							</InlineDialog>
						) : (
							<InlineDialog
								isOpen={isInvalid}
								content={<div>{errorMessage}</div>}
								placement="right"
							>
								<AkSelect
									// @ts-expect-error - TS2322 - Type 'MutableRefObject<unknown>' is not assignable to type '((string | ((instance: { components: Partial<SelectComponents<CalendarSelectOption, false, GroupTypeBase<CalendarSelectOption>>>; ... 23 more ...; UNSAFE_componentWillUpdate?(nextProps: Readonly<...>, nextState: Readonly<...>, nextContext: any): void; } | null) => void) | RefObject<...>) & (((instance: any) => void)...'.
									ref={akSelectRef}
									inputId={rest.id}
									// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
									options={calendarOptions as CalendarSelectOption[]}
									components={{
										ValueContainer,
									}}
									aria-label={formatMessage(messages.selectCalendar)}
									// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
									value={value as CalendarSelectOption}
									data-testid="servicedesk-sla-settings.ui.metrics.metric.editable.form.goals.goal.fields.calendar.ak-select"
									{...rest}
									onChange={(...args) => {
										// @ts-expect-error - TS2554 - Expected 1 arguments, but got 2.
										rest.onChange(...args);
										onChange();
									}}
									appearance={isSLAHierarchicalGoalsEnabled ? 'subtle' : 'default'}
									styles={customStyles}
								/>
							</InlineDialog>
						)
					}
					readView={() => (
						<ValueContainer>
							<ReadViewContainer
								data-testid="servicedesk-sla-settings.ui.metrics.metric.editable.form.goals.goal.fields.calendar.read-view-container"
								isEditMode
							>
								{value?.label}
							</ReadViewContainer>
						</ValueContainer>
					)}
					isEditing={isEditing}
					// eslint-disable-next-line @typescript-eslint/no-empty-function
					onConfirm={() => {}}
					onEdit={onEditRequested}
					hideActionButtons
					readViewFitContainerWidth
				/>
			)}
		</AkField>
	);
};

export const ValueContainer = ({ children }: { children: ReactNode }) => (
	<CalendarContainer>
		<CalendarIcon label="" />
		{children}
	</CalendarContainer>
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ReadViewContainer = styled.div<{ isEditMode: boolean }>(
	{
		maxWidth: '100%',
		wordBreak: 'break-word',
		display: 'inline-block',
		paddingLeft: token('space.075', '6px'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ isEditMode }) =>
		isEditMode &&
		`
    padding-top: 12px;
    padding-bottom: 12px;
  `,
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CalendarContainer = styled.div({
	display: 'flex',
	alignItems: 'center',
});

const customStyles: StylesConfig<CalendarSelectOption> = {
	control: (styles) => ({
		...styles,
		flexWrap: 'nowrap',
	}),
	valueContainer: (styles) => ({
		...styles,
		display: 'flex',
	}),
};
